// Screentone threshold map for `pen='tone'` — clustered-dot ordered dither
// (texture-brush-design.md §12.2 / consult 2026-06-20 段階1).
//
// ## Pitch parameterization (phase11-light-pens-2026-08-21.md §4.5/§4.6, 2026-08-21)
//
// The dot-grid cell size (`TONE_CELL`, world px) used to be a single
// hardcoded `4` — too fine to read as a dot PATTERN (§4.3: at pitch 4 the
// grid reads as grey noise, not water-mark dots, no matter which 番手 you
// pick). It is now a per-stroke `penParams.tone.pitch` (optional, one of
// `TONE_PITCHES`), read the SAME way `crayon`/`pastel`'s `grain` selects one
// of a few pre-baked maps — **NOT** a runtime kernel parameter. Each pitch
// gets its own pre-baked `TONE_THRESHOLD_SIZE × TONE_THRESHOLD_SIZE` map,
// built ONCE at module load and cached in `TONE_THRESHOLD_SAMPLERS` (same
// "don't re-bake per stroke" discipline `CRAYON_THRESHOLD_SAMPLERS` /
// `PASTEL_TEXTURE_SAMPLERS` already established) — the per-pixel kernel
// (`threshold(worldX, worldY) < densityLevel`) is completely unchanged; only
// WHICH pre-baked map it samples changes.
//
// `pitch` omitted on the wire → `TONE_DEFAULT_PITCH` (= `4`, the OLD
// hardcoded `TONE_CELL` value) — NOT the UI's own default (which the web
// layer, not this file, will pick — expected to be 16 or higher once tone's
// UI chip is un-commented; `TONE_PITCHES` includes `8` as the seamless-
// tiling-valid neighbour but §4.3/§4.5 found `4` AND `8` both read as noise,
// not a dot pattern — the UI layer is expected to only ever offer `16`/`32`/
// `64`). The `4` fallback here exists ONLY so already-recorded tone strokes
// that predate this field keep replaying byte-identically (replay fidelity,
// §4.6) — it is a DIFFERENT concern from "what should a brand-new UI
// default to".
//
// ## Why a threshold map (not a static dot array)
//
// The old screentone was three static 網点 alpha arrays (薄/中/濃 = fixed dot
// alpha), shipped as `texture` presets (`tone-dot-*`). §12.2 promotes トーン to
// its own `pen='tone'` category with a **continuous 番手 (density)**: one
// world-mapped **threshold matrix** + a per-stroke `density` reproduces EVERY
// 番手 by growing the inked dot from its centre outward as density rises — the
// exact behaviour of a real halftone screen.
//
// ## Clustered-dot ordered dither
//
// The matrix holds a per-texel **threshold** 0..255. A texel is inked when
//
//     threshold(worldX mod W, worldY mod H) < densityLevel
//
// (`densityLevel = round(density * 256)`, quantised IDENTICALLY in JS + Wasm —
// see the kernel). To make a dot that GROWS from the centre, each cell's
// thresholds are laid out **low at the cell centre, high at the cell corners**
// (= clustered dot). At low density only the few low-threshold centre texels
// pass → a small dot; as density rises the dot fattens toward the corners until
// the whole cell inks (density=1 ⇒ every threshold < 256 ⇒ full coverage).
//
// ## Seamless tiling (§12 / §9 invariant)
//
// The cell size MUST divide PRESET_TEXTURE_SIZE (256 = 2^8) so the matrix tiles
// the world plane with no phase jump across the texel 255 → 0 wrap. The map is
// built by replicating one `CELL × CELL` cell, and `CELL` divides 256, so world
// pixel 256 reads texel 0 with the same in-cell phase as texel 0 → continuous.
//
// ## Determinism (ABSOLUTE — texture-brush-design.md §3)
//
// The matrix is generated by a fixed integer formula (no `Math.random`, no
// float transcendental). Web (V8) and the snapshot worker (Skia/Wasm) bake the
// byte-identical `Uint8Array`. The kernel compares `threshold < densityLevel`
// with `densityLevel` quantised the SAME way on both sides, so the ink/no-ink
// decision is byte-identical → snapshot/late-join parity holds.
/** Tile size — same 256 world period as every other world-mapped asset. */
export const TONE_THRESHOLD_SIZE = 256;
/**
 * Selectable halftone cell sizes in texels (= screen frequency / pitch,
 * penWidth 非追従 / screen 固定周波数, §12.2). EVERY value MUST divide
 * `TONE_THRESHOLD_SIZE` (256 = 2^8) so each pre-baked map tiles seamlessly
 * (§4.5 unbroken invariant, same reasoning as `CRAYON_GRAIN_DECAY_NUMS`'s
 * cell-size doc).
 *
 * `4` is the OLD hardcoded `TONE_CELL` value, kept ONLY as the wire-omitted
 * replay fallback (`TONE_DEFAULT_PITCH`, §4.6) — not a UI option. `8` is
 * likewise too fine to read as a dot pattern (§4.3: "間隔4pxでは、どの番手を
 * 選んでも柄として成立しない" — §4.4's history note found 8 "全然荒い" when
 * judged as a SHADING tone, but re-judged as a DECORATIVE dot pattern it
 * still reads as noise, not water-mark dots). `16` / `32` / `64` are the
 * pattern-legible pitches §4.5 intends the UI to offer.
 */
export const TONE_PITCHES = [4, 8, 16, 32, 64];
/**
 * Wire `penParams.tone.pitch` omitted (or `penParams` missing/mismatched) →
 * this value. Equals the OLD hardcoded `TONE_CELL` so every already-recorded
 * tone stroke (predating this field) keeps replaying byte-identically
 * (§4.6 replay fidelity) — deliberately NOT the UI's own default (expected
 * to be `16`, a web-layer concern this file doesn't own).
 */
export const TONE_DEFAULT_PITCH = 4;
/**
 * Build a single `cell × cell` clustered-dot threshold cell (`cell` = pitch,
 * one of `TONE_PITCHES`). Thresholds rise with the Chebyshev-ish distance
 * from the cell centre so the inked region grows from the centre outward as
 * density increases.
 *
 * We compute, per intra-cell position, a **rank** ordering texels from the
 * centre (rank 0 = inked first / lowest threshold) to the corners (rank
 * cell²−1 = inked last / highest threshold), then map rank → threshold so the
 * thresholds are spread evenly across 0..255. Mapping:
 *
 *     threshold = clamp(floor((rank + 1) * 256 / (cell² + 1)), 1, 255)
 *
 * which yields non-decreasing values in [1, 255] (never 0, so density=0 ⇒
 * `densityLevel=0` ⇒ NO texel passes `threshold < 0` ⇒ empty; density=1 ⇒
 * `densityLevel=256` ⇒ every texel passes ⇒ full). Pure integer ops →
 * deterministic.
 *
 * **The clamp matters once `cell² ≥ 256`** (pitch 16/32/64: 256/1024/4096
 * texels per cell) — spreading MORE than 255 distinct ranks across only 255
 * legal non-0/non-256 threshold values necessarily makes some ranks share a
 * threshold (several texels ink on the SAME density step, ties broken purely
 * by the `densityLevel` compare, not by this map). Un-clamped, the low end of
 * the range rounds to `0` for `cell = 16` (`rank=0` → `floor(256/257) = 0`),
 * which is exactly the same class of bug §2.5's "低周波が支配すると壊れる"
 * lesson describes for crayon — caught here by
 * `toneThreshold.test.ts`'s "every threshold is in [1, 255]" case swept
 * across every `TONE_PITCHES` entry (not just the original `cell = 4`, whose
 * small `n` happened to never trip the unclamped formula).
 */
const buildClusteredDotCell = (cell) => {
    const n = cell * cell;
    // Centre of the cell in continuous coords (e.g. cell=4 → centre at 1.5,1.5).
    const c = (cell - 1) / 2;
    // Rank texels by squared distance from the centre. Use integer squared
    // distance ×4 (so the (cell-1)/2 half is integral after ×2) → ties broken by
    // a deterministic raster index to make a total order (stable, byte-identical).
    const order = [];
    for (let y = 0; y < cell; y++) {
        for (let x = 0; x < cell; x++) {
            const dx = 2 * x - (cell - 1); // = (x - c) * 2, integer
            const dy = 2 * y - (cell - 1);
            const d2 = dx * dx + dy * dy; // integer, ×4 the real squared distance
            const idx = y * cell + x;
            // key = primary d2, secondary raster idx (deterministic tie-break).
            order.push({ idx, key: d2 * n + idx });
        }
    }
    order.sort((a, b) => a.key - b.key);
    const out = new Uint8Array(n);
    for (let rank = 0; rank < n; rank++) {
        const entry = order[rank];
        if (entry === undefined)
            continue;
        // Even spread across 1..255, clamped (see doc — the clamp is a no-op for
        // cell=4 but load-bearing for cell ∈ {16, 32, 64}).
        const raw = Math.floor(((rank + 1) * 256) / (n + 1));
        out[entry.idx] = raw < 1 ? 1 : raw > 255 ? 255 : raw;
    }
    // c is used only to document the centre; silence unused-var without runtime cost.
    void c;
    return out;
};
/**
 * Tile the clustered-dot cell across the full `SIZE × SIZE` world plane.
 * Because `cell` (= pitch) is always a `TONE_PITCHES` member and every
 * member divides `TONE_THRESHOLD_SIZE`, the replication is seamless for
 * every pitch.
 */
const buildToneThresholdMap = (size, cell) => {
    const cellMap = buildClusteredDotCell(cell);
    const out = new Uint8Array(size * size);
    for (let y = 0; y < size; y++) {
        const cy = y % cell;
        for (let x = 0; x < size; x++) {
            const cx = x % cell;
            out[y * size + x] = cellMap[cy * cell + cx] ?? 0;
        }
    }
    return out;
};
/**
 * One pre-baked `TextureSampler` per `TONE_PITCHES` entry, built ONCE at
 * module load and indexed by `tonePitchIndex` — mirrors
 * `CRAYON_THRESHOLD_SAMPLERS` / `PASTEL_TEXTURE_SAMPLERS`'s "cache the
 * family, don't rebuild per stroke" discipline (§4.5 point 3). The kernel
 * reads `alpha[...]` as a **threshold** (not a multiplier) when the stroke
 * is `pen='tone'`. World mapping + `scale: 1` pins the dot grid to canvas
 * coords (penWidth 非追従, screen 固定周波数, §12.2) so overlapping tone
 * strokes at the SAME pitch keep the網点 aligned. Registered in the Wasm
 * texture registry like any other asset family (the byte arrays are the
 * SAME generator output on both sides → parity).
 */
export const TONE_THRESHOLD_SAMPLERS = TONE_PITCHES.map((pitch) => ({
    mapping: 'world',
    width: TONE_THRESHOLD_SIZE,
    height: TONE_THRESHOLD_SIZE,
    alpha: buildToneThresholdMap(TONE_THRESHOLD_SIZE, pitch),
    scale: 1,
}));
/**
 * Resolve a wire-level `pitch` (from `penParams.tone.pitch`, optional) into
 * a valid index of `TONE_THRESHOLD_SAMPLERS` / `TONE_PITCHES`. `undefined` →
 * `TONE_DEFAULT_PITCH` (§4.6 replay fallback). A value that isn't a member of
 * `TONE_PITCHES` (defensive — the protocol schema is expected to constrain
 * this, this is the render-side belt, same posture as `crayonGrainIndex`)
 * ALSO falls back to `TONE_DEFAULT_PITCH`, rather than clamping to the
 * nearest neighbour — an unrecognised pitch has no natural "nearest" the way
 * an out-of-range grain index does.
 */
export const tonePitchIndex = (pitch) => {
    const resolved = pitch ?? TONE_DEFAULT_PITCH;
    const idx = TONE_PITCHES.indexOf(resolved);
    return idx >= 0 ? idx : TONE_PITCHES.indexOf(TONE_DEFAULT_PITCH);
};
/**
 * Quantise a continuous density 0..1 (`penParams.density`) into the integer
 * threshold-compare level 0..256. A texel inks when `threshold < densityLevel`.
 *
 * MUST be byte-identical in JS + Wasm (the kernel determinism rule): both sides
 * compute `round(density * 256)` clamped to 0..256. `density=0 → 0` (empty:
 * thresholds are ≥1 so none pass), `density=1 → 256` (full: thresholds are ≤255
 * so all pass). The single `Math.round` is the one quantisation point.
 */
export const toneDensityLevel = (density) => {
    const clamped = density < 0 ? 0 : density > 1 ? 1 : density;
    const level = Math.round(clamped * 256);
    return level < 0 ? 0 : level > 256 ? 256 : level;
};
//# sourceMappingURL=toneThreshold.js.map